import { useState, useEffect, useCallback } from 'react';
import type { Plan, Subscription, Quota, UsageResponse, Invoice, BillingInterval, DailyActivity, PlanChangePreview } from '../types';
import * as billingApi from '../api/billing';
import { handleApiError } from '../api/client';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and cache available subscription plans.
 */
export function usePlans() {
  const [state, setState] = useState<AsyncState<Plan[]>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchPlans = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const plans = await billingApi.getPlans();
      setState({ data: plans, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error: handleApiError(error) });
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return { ...state, refetch: fetchPlans };
}

/**
 * Hook to fetch current subscription status.
 */
export function useSubscription(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const [state, setState] = useState<AsyncState<Subscription>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchSubscription = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const subscription = await billingApi.getSubscription();
      setState({ data: subscription, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error: handleApiError(error) });
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    fetchSubscription();
  }, [enabled, fetchSubscription]);

  const subscribe = useCallback(async (planId: string, interval: BillingInterval) => {
    try {
      const subscription = await billingApi.subscribe(planId, interval);
      setState({ data: subscription, loading: false, error: null });
      return subscription;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const cancel = useCallback(async (immediately = false) => {
    try {
      await billingApi.cancelSubscription(immediately);
      // Refetch to get updated status
      await fetchSubscription();
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, [fetchSubscription]);

  const previewChange = useCallback(async (planId: string, interval: BillingInterval): Promise<PlanChangePreview> => {
    try {
      return await billingApi.previewPlanChange(planId, interval);
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }, []);

  const applyChange = useCallback(async (planId: string, interval: BillingInterval) => {
    try {
      const subscription = await billingApi.applyPlanChange(planId, interval);
      setState({ data: subscription, loading: false, error: null });
      return subscription;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const cancelPendingChange = useCallback(async () => {
    try {
      await billingApi.cancelPendingChange();
      // Refetch to get updated status
      await fetchSubscription();
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, [fetchSubscription]);

  const startCheckout = useCallback(async (planId: string, interval: BillingInterval, promotionCode?: string) => {
    try {
      const { checkout_url } = await billingApi.createCheckoutSession(planId, interval, undefined, undefined, promotionCode);
      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { portal_url } = await billingApi.createCustomerPortal();
      // Redirect to Stripe Customer Portal
      window.location.href = portal_url;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  return { ...state, refetch: fetchSubscription, subscribe, cancel, previewChange, applyChange, cancelPendingChange, startCheckout, openCustomerPortal };
}

/**
 * Hook to fetch usage quota.
 */
export function useQuota() {
  const [state, setState] = useState<AsyncState<Quota>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchQuota = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const quota = await billingApi.getQuota();
      setState({ data: quota, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error: handleApiError(error) });
    }
  }, []);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  return { ...state, refetch: fetchQuota };
}

/**
 * Hook to fetch usage history.
 */
export function useUsage(start?: string, end?: string) {
  const [state, setState] = useState<AsyncState<UsageResponse>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchUsage = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const usage = await billingApi.getUsage(start, end);
      setState({ data: usage, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error: handleApiError(error) });
    }
  }, [start, end]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return { ...state, refetch: fetchUsage };
}

/**
 * Hook to fetch invoice history with pagination.
 */
export function useInvoices(initialLimit = 20) {
  const [state, setState] = useState<AsyncState<{ invoices: Invoice[]; total: number }>>({
    data: null,
    loading: true,
    error: null,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(initialLimit);

  const fetchInvoices = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await billingApi.getInvoices(limit, offset);
      setState({ data: result, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error: handleApiError(error) });
    }
  }, [limit, offset]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const nextPage = useCallback(() => {
    if (state.data && offset + limit < state.data.total) {
      setOffset((prev) => prev + limit);
    }
  }, [state.data, offset, limit]);

  const prevPage = useCallback(() => {
    setOffset((prev) => Math.max(0, prev - limit));
  }, [limit]);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = state.data ? Math.ceil(state.data.total / limit) : 0;

  return {
    ...state,
    refetch: fetchInvoices,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    hasNextPage: state.data ? offset + limit < state.data.total : false,
    hasPrevPage: offset > 0,
  };
}

/**
 * Hook to fetch the last 7 days of transcription activity.
 */
export function useUsageActivity() {
  const [state, setState] = useState<AsyncState<DailyActivity[]>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchActivity = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const activity = await billingApi.getUsageActivity();
      setState({ data: activity, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error: handleApiError(error) });
    }
  }, []);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  return { ...state, refetch: fetchActivity };
}
